import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getDeck, updateDeck, deckComments } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, ArrowUp, ArrowDown, Save, Play, Link as LinkIcon, MessageSquare, Copy as CopyIcon, Type, List as ListIcon, Columns2, Image as ImageIcon, Quote as QuoteIcon, Flag, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import SlideView from "@/components/SlideView";

const LAYOUTS = [
  { key: "brand_cover", label: "Brand Cover", icon: Sparkles },
  { key: "title", label: "Title", icon: Type },
  { key: "bullets", label: "Bullets", icon: ListIcon },
  { key: "two_column", label: "Two Column", icon: Columns2 },
  { key: "image", label: "Image", icon: ImageIcon },
  { key: "quote", label: "Quote", icon: QuoteIcon },
  { key: "closing", label: "Closing", icon: Flag },
  { key: "brand_closing", label: "Brand Closing", icon: Sparkles },
];

const emptySlide = (layout = "title") => ({
  id: `s_${Math.random().toString(36).slice(2, 10)}`,
  layout, title: "", subtitle: "", body: "", body2: "",
  bullets: [], image_url: "", caption: "", attribution: "", notes: "", background_color: "",
});

export default function DeckEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deck, setDeck] = useState(null);
  const [current, setCurrent] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState([]);
  const shareInput = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await getDeck(id);
        setDeck(d);
        setCurrent(0);
        try { const c = await deckComments(id); setComments(c); } catch { /* no comments yet */ }
      } catch { toast.error("Failed to load deck"); navigate("/admin"); }
    })();
  }, [id, navigate]);

  const slide = deck?.slides?.[current];
  const publicUrl = deck ? `${window.location.origin}/d/${deck.slug}` : "";
  const slideComments = useMemo(
    () => comments.filter((c) => c.slide_id === slide?.id),
    [comments, slide]
  );

  const patchDeck = (fn) => { setDeck((d) => { const n = fn(d); setDirty(true); return n; }); };
  const patchSlide = (fn) => patchDeck((d) => {
    const slides = d.slides.map((s, i) => (i === current ? fn(s) : s));
    return { ...d, slides };
  });

  const addSlide = () => patchDeck((d) => { const slides = [...d.slides, emptySlide(slide?.layout || "title")]; setTimeout(() => setCurrent(slides.length - 1), 0); return { ...d, slides }; });
  const removeSlide = (idx) => patchDeck((d) => {
    if (d.slides.length <= 1) { toast.error("A deck needs at least one slide."); return d; }
    const slides = d.slides.filter((_, i) => i !== idx);
    const nextCur = Math.max(0, idx === current ? idx - 1 : current > idx ? current - 1 : current);
    setTimeout(() => setCurrent(Math.min(nextCur, slides.length - 1)), 0);
    return { ...d, slides };
  });
  const moveSlide = (idx, delta) => patchDeck((d) => {
    const j = idx + delta; if (j < 0 || j >= d.slides.length) return d;
    const slides = [...d.slides]; [slides[idx], slides[j]] = [slides[j], slides[idx]];
    setTimeout(() => setCurrent(j), 0);
    return { ...d, slides };
  });

  const save = async () => {
    if (!deck) return;
    setSaving(true);
    try {
      const updated = await updateDeck(deck.id, {
        title: deck.title, description: deck.description, is_public: deck.is_public,
        theme: deck.theme, slides: deck.slides,
      });
      setDeck(updated); setDirty(false); toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const togglePublic = async (val) => {
    if (!deck) return;
    // Save current state first, then toggle
    try {
      const updated = await updateDeck(deck.id, { ...deck, is_public: val, slides: deck.slides });
      setDeck(updated); setDirty(false); toast.success(val ? "Public link enabled" : "Deck set to private");
    } catch { toast.error("Failed to update"); }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); }
    catch { if (shareInput.current) { shareInput.current.select(); document.execCommand("copy"); toast.success("Link copied"); } }
  };

  const openPresenter = () => {
    if (!deck.is_public) { toast.error("Enable the public link first."); return; }
    window.open(`/d/${deck.slug}?present=1`, "_blank");
  };

  const exportPdf = () => window.print();

  if (!deck) return (<div className="min-h-screen flex items-center justify-center bg-[var(--layor-ivory)]"><div className="text-sm text-[var(--layor-muted)]">Loading deck…</div></div>);

  return (
    <div className="min-h-screen bg-[var(--layor-ivory)]" data-testid="page-deck-editor">
      {/* Top bar */}
      <div className="border-b border-[var(--layor-border)] bg-white sticky top-0 z-30 print:hidden">
        <div className="mx-auto max-w-[1400px] px-4 md:px-6 h-14 flex items-center gap-3">
          <Link to="/admin" className="text-[var(--layor-muted)] hover:text-[var(--layor-navy)]" data-testid="back-to-admin"><ArrowLeft size={18} /></Link>
          <input
            value={deck.title}
            onChange={(e) => patchDeck((d) => ({ ...d, title: e.target.value }))}
            className="flex-1 min-w-0 text-[15px] font-semibold text-[var(--layor-navy)] bg-transparent focus:outline-none border border-transparent focus:border-[var(--layor-border)] rounded-md px-2 py-1"
            data-testid="deck-title-input"
          />
          <div className="hidden md:flex items-center gap-2 text-xs text-[var(--layor-muted)]">
            <Switch checked={!!deck.is_public} onCheckedChange={togglePublic} data-testid="toggle-public" />
            <span>Public link</span>
          </div>
          {deck.is_public && (
            <div className="hidden lg:flex items-center gap-2">
              <input ref={shareInput} value={publicUrl} readOnly className="h-8 text-xs w-64 px-2 border border-[var(--layor-border)] rounded-md bg-[var(--layor-gray)]" data-testid="public-url" />
              <button onClick={copyLink} className="btn-ghost inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold" data-testid="copy-link"><CopyIcon size={12} /> Copy</button>
            </div>
          )}
          <button onClick={openPresenter} className="btn-ghost inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold" data-testid="present-btn"><Play size={12} /> Present</button>
          <button onClick={exportPdf} className="btn-ghost inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold" data-testid="export-pdf">PDF</button>
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold disabled:opacity-60" data-testid="save-deck">
            <Save size={12} /> {saving ? "Saving…" : dirty ? "Save" : "Saved"}
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-6 py-6 grid grid-cols-12 gap-4 print:block">
        {/* Slide list */}
        <aside className="col-span-12 md:col-span-3 print:hidden">
          <div className="bg-white border border-[var(--layor-border)] rounded-2xl p-3 space-y-2 max-h-[calc(100vh-100px)] overflow-auto sticky top-16">
            <div className="flex items-center justify-between px-1">
              <div className="eyebrow">Slides ({deck.slides.length})</div>
              <button onClick={addSlide} className="text-[var(--layor-blue)] hover:underline text-xs font-semibold inline-flex items-center gap-1" data-testid="add-slide"><Plus size={12} /> Add</button>
            </div>
            {deck.slides.map((s, i) => (
              <div key={s.id} className={`group relative border rounded-xl p-2 cursor-pointer ${i === current ? "border-[var(--layor-navy)] bg-[var(--layor-gray)]" : "border-[var(--layor-border)] bg-white"}`}
                onClick={() => setCurrent(i)} data-testid={`slide-thumb-${i}`}>
                <div className="text-[10px] text-[var(--layor-muted)] uppercase tracking-widest">{i + 1} · {s.layout.replace("_", " ")}</div>
                <div className="mt-1 text-xs font-semibold text-[var(--layor-navy)] line-clamp-2">{s.title || "Untitled"}</div>
                <div className="mt-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={(e) => { e.stopPropagation(); moveSlide(i, -1); }} className="h-6 w-6 rounded border border-[var(--layor-border)] inline-flex items-center justify-center"><ArrowUp size={10} /></button>
                  <button onClick={(e) => { e.stopPropagation(); moveSlide(i, +1); }} className="h-6 w-6 rounded border border-[var(--layor-border)] inline-flex items-center justify-center"><ArrowDown size={10} /></button>
                  <button onClick={(e) => { e.stopPropagation(); removeSlide(i); }} className="h-6 w-6 rounded border border-red-200 text-red-500 inline-flex items-center justify-center"><Trash2 size={10} /></button>
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* Editor + preview */}
        <main className="col-span-12 md:col-span-9 space-y-4">
          {/* Preview */}
          <div className="bg-white border border-[var(--layor-border)] rounded-2xl overflow-hidden">
            <div className="aspect-[16/9] w-full">
              <SlideView slide={slide} theme={deck.theme} />
            </div>
          </div>

          {/* Layout picker */}
          <div className="bg-white border border-[var(--layor-border)] rounded-2xl p-3 flex flex-wrap gap-2 print:hidden">
            {LAYOUTS.map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => patchSlide((s) => ({ ...s, layout: key }))}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-semibold border ${slide.layout === key ? "bg-[var(--layor-navy)] text-white border-[var(--layor-navy)]" : "bg-white text-[var(--layor-navy)] border-[var(--layor-border)] hover:border-[var(--layor-navy)]"}`}
                data-testid={`layout-${key}`}
              >
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>

          {/* Slide fields */}
          <div className="bg-white border border-[var(--layor-border)] rounded-2xl p-5 grid sm:grid-cols-2 gap-4 print:hidden">
            <div className="sm:col-span-2"><Label>Title</Label><Input value={slide.title || ""} onChange={(e) => patchSlide((s) => ({ ...s, title: e.target.value }))} data-testid="slide-title" /></div>

            {slide.layout === "title" && (
              <div className="sm:col-span-2"><Label>Subtitle</Label><Input value={slide.subtitle || ""} onChange={(e) => patchSlide((s) => ({ ...s, subtitle: e.target.value }))} data-testid="slide-subtitle" /></div>
            )}
            {(slide.layout === "closing" || slide.layout === "brand_cover" || slide.layout === "brand_closing") && (
              <div className="sm:col-span-2"><Label>Subtitle / CTA</Label><Input value={slide.subtitle || ""} onChange={(e) => patchSlide((s) => ({ ...s, subtitle: e.target.value }))} /></div>
            )}
            {slide.layout === "bullets" && (
              <div className="sm:col-span-2">
                <Label>Bullets (one per line)</Label>
                <Textarea rows={6} value={(slide.bullets || []).join("\n")} onChange={(e) => patchSlide((s) => ({ ...s, bullets: e.target.value.split("\n").map((b) => b).filter((b) => b !== undefined) }))} data-testid="slide-bullets" />
              </div>
            )}
            {slide.layout === "two_column" && (
              <>
                <div><Label>Left column</Label><Textarea rows={5} value={slide.body || ""} onChange={(e) => patchSlide((s) => ({ ...s, body: e.target.value }))} /></div>
                <div><Label>Right column</Label><Textarea rows={5} value={slide.body2 || ""} onChange={(e) => patchSlide((s) => ({ ...s, body2: e.target.value }))} /></div>
              </>
            )}
            {slide.layout === "image" && (
              <>
                <div className="sm:col-span-2"><Label>Image URL</Label><Input value={slide.image_url || ""} onChange={(e) => patchSlide((s) => ({ ...s, image_url: e.target.value }))} placeholder="https://…" data-testid="slide-image-url" /></div>
                <div className="sm:col-span-2"><Label>Caption</Label><Input value={slide.caption || ""} onChange={(e) => patchSlide((s) => ({ ...s, caption: e.target.value }))} /></div>
              </>
            )}
            {slide.layout === "quote" && (
              <>
                <div className="sm:col-span-2"><Label>Quote</Label><Textarea rows={4} value={slide.body || ""} onChange={(e) => patchSlide((s) => ({ ...s, body: e.target.value }))} /></div>
                <div className="sm:col-span-2"><Label>Attribution</Label><Input value={slide.attribution || ""} onChange={(e) => patchSlide((s) => ({ ...s, attribution: e.target.value }))} /></div>
              </>
            )}

            <div className="sm:col-span-2 border-t border-[var(--layor-border)] pt-4">
              <div className="eyebrow">Speaker notes</div>
              <Textarea rows={3} value={slide.notes || ""} onChange={(e) => patchSlide((s) => ({ ...s, notes: e.target.value }))} placeholder="Notes for you, hidden from viewers" />
            </div>
          </div>

          {/* Theme + Comments */}
          <div className="grid md:grid-cols-2 gap-4 print:hidden">
            <div className="bg-white border border-[var(--layor-border)] rounded-2xl p-5">
              <div className="eyebrow">Brand colors</div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <ColorField label="Primary" value={deck.theme.primary_color} onChange={(v) => patchDeck((d) => ({ ...d, theme: { ...d.theme, primary_color: v } }))} />
                <ColorField label="Accent" value={deck.theme.accent_color} onChange={(v) => patchDeck((d) => ({ ...d, theme: { ...d.theme, accent_color: v } }))} />
                <ColorField label="Text" value={deck.theme.text_color} onChange={(v) => patchDeck((d) => ({ ...d, theme: { ...d.theme, text_color: v } }))} />
                <ColorField label="Background" value={deck.theme.background_color} onChange={(v) => patchDeck((d) => ({ ...d, theme: { ...d.theme, background_color: v } }))} />
              </div>
              <p className="mt-3 text-xs text-[var(--layor-muted)]">These colors apply automatically to every slide in this deck.</p>
            </div>

            <div className="bg-white border border-[var(--layor-border)] rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <div className="eyebrow">Client comments on this slide</div>
                <span className="text-xs text-[var(--layor-muted)] inline-flex items-center gap-1"><MessageSquare size={12} /> {slideComments.length}</span>
              </div>
              {slideComments.length === 0 ? (
                <p className="mt-3 text-sm text-[var(--layor-muted)]">No comments yet. Share the public link to collect feedback.</p>
              ) : (
                <ul className="mt-3 space-y-3 max-h-56 overflow-auto">
                  {slideComments.map((c) => (
                    <li key={c.id} className="border border-[var(--layor-border)] rounded-xl p-3">
                      <div className="text-xs font-semibold text-[var(--layor-navy)]">{c.name} <span className="text-[var(--layor-muted)] font-normal">· {new Date(c.created_at).toLocaleString()}</span></div>
                      <div className="mt-1 text-sm text-[var(--layor-navy)]">{c.body}</div>
                    </li>
                  ))}
                </ul>
              )}
              {deck.is_public && (
                <div className="mt-4 flex items-center gap-2">
                  <LinkIcon size={12} className="text-[var(--layor-muted)]" />
                  <a href={publicUrl} target="_blank" rel="noreferrer" className="text-xs text-[var(--layor-blue)] hover:underline">{publicUrl}</a>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Print stylesheet: each slide becomes a landscape page */}
      <style>{`
        @media print {
          @page { size: landscape; margin: 0; }
          body, .min-h-screen { background: #fff !important; }
          .print\\:hidden { display: none !important; }
          .print\\:block { display: block !important; }
          .print-deck { display: block; }
          .print-slide { page-break-after: always; width: 100vw; height: 100vh; }
        }
      `}</style>

      {/* Hidden printable content */}
      <div className="hidden print:block print-deck">
        {deck.slides.map((s) => (
          <div key={s.id} className="print-slide">
            <SlideView slide={s} theme={deck.theme} />
          </div>
        ))}
      </div>
    </div>
  );
}

const ColorField = ({ label, value, onChange }) => (
  <div>
    <Label>{label}</Label>
    <div className="mt-1 flex items-center gap-2">
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="h-9 w-10 rounded border border-[var(--layor-border)] cursor-pointer" />
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="text-xs" />
    </div>
  </div>
);
